import type { IssueSeverity, ScheduleValidationIssue } from "../types";

/**
 * Codes whose count reflects affected STUDENTS rather than issue rows. "Could
 * not place" emits one issue per course, so a row count answers "how many
 * courses" — admins want "how many student placements failed", which is the
 * sum of each course's roster.
 */
const STUDENT_SUM_BADGE_CODES = new Set<string>(["Scheduler.Student.NotPlaced"]);

/**
 * The weight a single issue contributes to EVERY displayed total — group badge,
 * section header, banner, tab badge, and the Run button chips. A
 * {@link STUDENT_SUM_BADGE_CODES} issue contributes its affected-student count
 * (each unplaced student is a distinct problem to resolve); every other issue
 * contributes 1. Routing all totals through this one weight is what makes them
 * reconcile — the group badges sum to the section header, which sums to the
 * banner, which matches the Run button's error+warning chips.
 */
export function issueUnitCount(issue: ScheduleValidationIssue): number {
  if (!STUDENT_SUM_BADGE_CODES.has(issue.code)) {
    return 1;
  }
  // Floor at 1: a rendered "could not place" row is at least one problem, so it
  // always counts — even in the rare stale case where the recomputed roster is
  // momentarily empty (all its students since placed, before the next re-run).
  // This keeps every visible card accounted for in the totals and stops a lone
  // such row from making the tab read "No issues found". With this floor, a
  // total is 0 iff there are literally no issue rows.
  return Math.max(1, issue.affectedStudents?.length ?? 0);
}

/** Total {@link issueUnitCount} across a set of issues. */
export function sumIssueUnits(
  issues: readonly ScheduleValidationIssue[],
): number {
  return issues.reduce((sum, issue) => sum + issueUnitCount(issue), 0);
}

/**
 * Weighted error/warning split (by {@link issueUnitCount}) for the Run button
 * chips and the preflight gate, so those numbers reconcile with the issue list.
 */
export function issueUnitCountsBySeverity(
  issues: readonly ScheduleValidationIssue[],
): { errors: number; warnings: number } {
  let errors = 0;
  let warnings = 0;
  for (const issue of issues) {
    const weight = issueUnitCount(issue);
    if (issue.severity === "Error") {
      errors += weight;
    } else {
      warnings += weight;
    }
  }
  return { errors, warnings };
}

/**
 * One bucket of issues sharing the same `(code, severity)` pair. The
 * Validation Issues tab renders one collapsible card per bucket.
 */
export interface IssueGroup {
  code: string;
  severity: IssueSeverity;
  issues: ScheduleValidationIssue[];
}

/**
 * Whether a group's header badge counts affected students (summed per course)
 * rather than issues. Drives the badge's accessible label wording.
 */
export function groupBadgeCountsStudents(group: IssueGroup): boolean {
  return STUDENT_SUM_BADGE_CODES.has(group.code);
}

/**
 * The number to show on a group's header badge — the {@link sumIssueUnits} of
 * its issues. For most groups that's the issue count; for
 * {@link STUDENT_SUM_BADGE_CODES} it's the sum of every issue's affected-student
 * roster (a student unplaced in two courses counts twice — a deliberate
 * per-course sum, not a distinct-student count). Same weight as every other
 * total on the tab, so the badges sum to the section/banner counts.
 */
export function groupBadgeCount(group: IssueGroup): number {
  return sumIssueUnits(group.issues);
}

/**
 * Accessible label for a group's header badge, disambiguating what the bare
 * number counts ("32 affected students" vs "2 issues") with correct
 * pluralization. Keeps the count/label/pluralization rules colocated.
 */
export function groupBadgeLabel(group: IssueGroup): string {
  const count = groupBadgeCount(group);
  const noun = groupBadgeCountsStudents(group) ? "affected student" : "issue";
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * Groups issues by `(code, severity)` preserving first-seen order. Pure
 * helper kept module-level (and in `lib/`) so it can be reused by render
 * code AND by the schedule-home banner ("Y categories") without dragging
 * the component in.
 */
export function groupIssuesByCodeSeverity(
  issues: ScheduleValidationIssue[],
): IssueGroup[] {
  const map = new Map<string, IssueGroup>();
  for (const issue of issues) {
    const key = `${issue.code}::${issue.severity}`;
    const existing = map.get(key);
    if (existing) {
      existing.issues.push(issue);
    } else {
      map.set(key, {
        code: issue.code,
        severity: issue.severity,
        issues: [issue],
      });
    }
  }
  return Array.from(map.values());
}

/**
 * Codes whose `message` carries a LIVE count that shrinks/changes as students
 * are placed — "could not place 22 students", "18 of 20 seats taken". Their
 * message must be excluded from {@link stableIssueKey}, or the row would remount
 * on every override (the exact collapse the stable key prevents). Each is
 * already uniquely identified by its typed columns (course, and section for the
 * capacity rules), so dropping the message costs no uniqueness.
 */
const VOLATILE_MESSAGE_CODES = new Set<string>([
  "Scheduler.Student.NotPlaced",
  "Scheduler.Section.CapacityExceeded",
  "Scheduler.Section.LowFill",
]);

/**
 * A stable React key / logical identity for an issue row that survives the
 * backend regenerating PostRun issues with fresh GUIDs. Applying a manual
 * override (placing a student) deletes and re-creates every PostRun issue for
 * the run, so `issue.id` changes on the next fetch; keying rows by `id` would
 * remount every row and reset local expand state — collapsing the affected-
 * student list the user was working through.
 *
 * Built from the typed context columns plus the `message`. The message is the
 * only place that carries a per-day rule's calendar DATE ("…on 2026-08-12…")
 * and a time-conflict's SECOND course — the details that distinguish otherwise-
 * identical rows (same teacher across weeks, same student's two long days, two
 * conflict pairs sharing a first course). It's stable across an unrelated
 * override, so folding it in keeps keys both unique and steady. It is dropped
 * only for {@link VOLATILE_MESSAGE_CODES}, whose message carries a live count
 * (and which are already unique by their typed columns).
 */
export function stableIssueKey(issue: ScheduleValidationIssue): string {
  const student =
    issue.studentIdentifier ?? issue.normalizedStudentIdentifier ?? "";
  const messagePart = VOLATILE_MESSAGE_CODES.has(issue.code)
    ? ""
    : issue.message;
  return [
    issue.code,
    issue.stage,
    issue.courseId ?? "",
    student,
    issue.sectionLabel ?? "",
    issue.teacherEmail ?? "",
    issue.eventId ?? "",
    messagePart,
  ].join("|");
}
